// ============================================
// PUBLIC ENVIRONMENT VARIABLES (browser + server)
// ============================================
import {env} from "next-runtime-env";

/**
 * Type-safe environment variable access helpers
 * Each environment variable has its own dedicated function
 */
// ============================================
// TYPES
// ============================================

/**
 * Get the public URL of the application
 * @default "http://localhost:3000"
 */
export function getPublicUrl(): string | undefined {
    return env("NEXT_PUBLIC_URL")!;
}

/**
 * Get the asset prefix for static files
 */
export function getAssetPrefix(): string {
    return env("NEXT_PUBLIC_ASSET_PREFIX") ?? "";
}

/**
 * Get the base path for the application
 * @default ""
 */
export function getBasePath(): string {
    return env("NEXT_PUBLIC_BASE_PATH") ?? "/";
}

/**
 * Get Google Maps API key
 * @throws Error if not defined
 */
export function getGoogleMapsApiKey(): string {
    return env("NEXT_PUBLIC_GOOGLE_MAPS_API_KEY")!;
}
export type AppMode = "production" | "staging" | "maintenance";

export function getAppMode(): AppMode {
    return (env("NEXT_PUBLIC_APP_MODE") as AppMode) ?? "staging";
}

export function isStaging(): boolean {
    return getAppMode() === "staging";
}

export function isProduction(): boolean {
    return getAppMode() === "production";
}

export function isMaintenance(): boolean {
    return getAppMode() === "maintenance";
}
/**
 * Get Google Tag Manager container ID
 * Example: GTM-XXXXXXX
 */
export function getGtmId(): string | undefined {
    return env("NEXT_PUBLIC_GTM_ID");
}

/**
 * Get the API base URL
 * NEXT_PUBLIC_API_URL should contain the full path including /api/v1
 * Example: https://vdp.vacasastage.io/casago-com-web/ui/api/v1
 * @throws Error if NEXT_PUBLIC_API_URL is not defined
 */
export function getApiUrl(): string {
    return env("NEXT_PUBLIC_API_URL")!;
}

/**
 * Get Vacasa API base URL
 * @default "https://vdp.vacasastage.io/booking-api/api/v1"
 */
export function getVacasaApiUrl(): string {
    try {
        return env("NEXT_PUBLIC_VACASA_API_URL") ?? "https://vdp.vacasastage.io/booking-api/api/v1";
    } catch {
        return "https://vdp.vacasastage.io/booking-api/api/v1";
    }
}

/**
 * Whether the promo/coupon input is visible in the UI.
 * Defaults to false — set NEXT_PUBLIC_COUPON_ENABLED=true to enable.
 */
export function isCouponEnabled(): boolean {
    return env("NEXT_PUBLIC_COUPON_ENABLED") === "true";
}

/**
 * Get Segment write key based on environment.
 * Uses NEXT_PUBLIC_SEGMENT_WRITE_KEY_PROD in production,
 * falls back to NEXT_PUBLIC_SEGMENT_WRITE_KEY_STAGING otherwise.
 * @default "" if neither is defined
 */

export function getSegmentWriteKey(): string {
    // const key = isProduction() ? env("NEXT_PUBLIC_SEGMENT_WRITE_KEY_PROD") : env("NEXT_PUBLIC_SEGMENT_WRITE_KEY_STAGING");

    // if (key) {
    //     return key;
    // }

    // Hardcoded fallback / no access to add/edit env vars.
    return "IAson2PqlkU9itQOIBlqTyqwIavrM3DN";
}

// ============================================
// SERVER-ONLY ENVIRONMENT VARIABLES
// ============================================

/**
 * Get Google Maps URL signing secret (server-only)
 * Used for signing Static Maps API URLs via HMAC-SHA1
 */
export function getGoogleMapsSigningSecret(): string | undefined {
    return process.env.google_maps_signing_secret;
}

/**
 * Check if bundle analyzer is enabled
 */
export function isAnalyzeEnabled(): boolean {
    return process.env.analyze === "true";
}

/**
 * Check if logger is enabled
 * @default true in development, false in production
 */
export function isEnableLogger(): boolean {
    return process.env.enable_logger === "true";
}

/**
 * Check if booking is disabled
 * Booking is allowed by default.
 * Set NEXT_PUBLIC_BOOKING_DISABLED=true to block bookings (e.g. on staging).
 */
export function isBookingDisabled(): boolean {
    return env("NEXT_PUBLIC_BOOKING_DISABLED") === "true";
}

/**
 * Whether the "Available Market" marker is visible on destination pages.
 * Defaults to false — set NEXT_PUBLIC_AVAILABLE_MARKET=true to enable.
 */
export function isAvailableMarketEnabled(): boolean {
    return env("NEXT_PUBLIC_AVAILABLE_MARKET") === "true";
}

export function getKountClientID(): string {
    return env("NEXT_PUBLIC_KOUNT_CLIENT_ID") ?? "101743";
}

export function getKountDebugModeEnabled(): boolean {
    return env("NEXT_PUBLIC_KOUNT_DEBUG_MODE_ENABLED") === "true";
}
